/**
 * Public-page SEO registry — WashPros™
 * =========================================================================
 * Per-route SEO config for the non-`/florida` public marketing surface
 * (homepage, about, pricing, franchise, contact). Mirrors the Florida
 * programmatic-page approach but as a static lookup since these are
 * one-off landing pages (not city × service combinatorial).
 *
 * Each entry returns the shape `useSeo()` consumes directly:
 *   { title, description, canonical, ogImage, ogType, twitterCard,
 *     jsonLd: [Organization, WebSite, BreadcrumbList, optional Service] }
 *
 * Invariants (Rick T+34 directive 2026-05-11):
 *   • Canonical always starts with https://washpros.io. NO defensive
 *     domains in copy or canonical URLs.
 *   • Brand names preserved verbatim: WashPros™, WashPros Fleet™,
 *     The Car Wash Concierge Company™, Wax and Shine Headquarters™.
 *   • Natural English copy weaves these search phrases at most once
 *     each per page (no stuffing):
 *       mobile car wash · mobile detailing · car wash pros ·
 *       car detailing · fleet washing · WashPros Fleet ·
 *       vehicle care · mobile wash services · boat detailing ·
 *       RV wash · golf cart cleaning · driveway car wash ·
 *       car wash concierge · on-demand car wash ·
 *       professional detailers · vendor marketplace ·
 *       fleet vehicle cleaning.
 *   • NO `<meta name="keywords">` emission anywhere.
 *   • Public valuation / fundraise / SAFE / guaranteed-returns
 *     language is forbidden (existing CI guard enforces).
 * =========================================================================
 */
import {
  CANONICAL_DOMAIN,
  ORG_NAME,
  ORG_LEGAL,
  ORG_BRAND_TITLE,
  ORG_TAGLINE,
  ORG_FLEET_BRAND,
} from "./floridaSeoKeywords";

const OG_IMAGE = `${CANONICAL_DOMAIN}/brand-assets/icon-512x512.png`;

const ORGANIZATION_LD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": ORG_NAME,
  "legalName": ORG_LEGAL,
  "url": CANONICAL_DOMAIN,
  "logo": OG_IMAGE,
  "brand": ORG_BRAND_TITLE,
  "slogan": ORG_TAGLINE,
};

const WEBSITE_LD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": ORG_NAME,
  "url": CANONICAL_DOMAIN,
  "publisher": { "@type": "Organization", "name": ORG_LEGAL },
};

const breadcrumb = (trail) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": trail.map((t, i) => ({
    "@type": "ListItem",
    "position": i + 1,
    "name": t.name,
    "item": t.url,
  })),
});

/* ─── Per-route SEO ────────────────────────────────────────────── */
export const PUBLIC_PAGE_SEO = {
  home: {
    title: `${ORG_BRAND_TITLE} — Mobile Car Wash, Detailing & Fleet Vehicle Care`,
    description: `${ORG_BRAND_TITLE} is ${ORG_TAGLINE}. Book on-demand mobile car wash and mobile detailing, plus fleet washing, boat detailing, and RV wash services — all through one concierge platform with approved vendors and transparent pricing.`,
    canonical: `${CANONICAL_DOMAIN}/`,
    ogImage: OG_IMAGE,
    ogType: "website",
    twitterCard: "summary_large_image",
    jsonLd: [
      ORGANIZATION_LD,
      WEBSITE_LD,
      breadcrumb([{ name: "Home", url: CANONICAL_DOMAIN }]),
    ],
  },

  about: {
    title: `About ${ORG_BRAND_TITLE} — ${ORG_TAGLINE}`,
    description: `${ORG_BRAND_TITLE} is a vendor marketplace for mobile car wash, mobile detailing, fleet washing, and specialty vehicle care. We connect customers with approved, insurance-verified professional detailers and mobile wash services across Florida and beyond.`,
    canonical: `${CANONICAL_DOMAIN}/about`,
    ogImage: OG_IMAGE,
    ogType: "website",
    twitterCard: "summary_large_image",
    jsonLd: [
      ORGANIZATION_LD,
      WEBSITE_LD,
      breadcrumb([
        { name: "Home", url: CANONICAL_DOMAIN },
        { name: "About", url: `${CANONICAL_DOMAIN}/about` },
      ]),
    ],
  },

  pricing: {
    title: `Transparent Pricing — Mobile Car Wash, Detailing & Fleet | ${ORG_BRAND_TITLE}`,
    description: `See how ${ORG_BRAND_TITLE} prices mobile car wash, mobile detailing, fleet washing, boat detailing, RV wash, and golf cart cleaning. Transparent commissions, vendor-friendly splits, and clear customer fees — no surprise charges.`,
    canonical: `${CANONICAL_DOMAIN}/pricing`,
    ogImage: OG_IMAGE,
    ogType: "website",
    twitterCard: "summary_large_image",
    jsonLd: [
      ORGANIZATION_LD,
      WEBSITE_LD,
      breadcrumb([
        { name: "Home", url: CANONICAL_DOMAIN },
        { name: "Pricing", url: `${CANONICAL_DOMAIN}/pricing` },
      ]),
    ],
  },

  franchise: {
    title: `${ORG_FLEET_BRAND} Market Partner Opportunities | ${ORG_BRAND_TITLE}`,
    description: `${ORG_FLEET_BRAND} — fleet vehicle cleaning, dealership detailing, marina detailing, and RV park services for commercial accounts. Submit interest to bring ${ORG_FLEET_BRAND} to your market through our vendor marketplace and dispatch platform.`,
    canonical: `${CANONICAL_DOMAIN}/franchise`,
    ogImage: OG_IMAGE,
    ogType: "website",
    twitterCard: "summary_large_image",
    jsonLd: [
      ORGANIZATION_LD,
      WEBSITE_LD,
      breadcrumb([
        { name: "Home", url: CANONICAL_DOMAIN },
        { name: "Franchise & Partners", url: `${CANONICAL_DOMAIN}/franchise` },
      ]),
    ],
  },

  contact: {
    title: `Contact ${ORG_BRAND_TITLE} — Customer, Vendor & Fleet Support`,
    description: `Reach ${ORG_BRAND_TITLE} support for mobile car wash bookings, vendor onboarding, fleet vehicle cleaning quotes, and concierge-level vehicle care. Customers, professional detailers, and fleet partners welcome.`,
    canonical: `${CANONICAL_DOMAIN}/contact`,
    ogImage: OG_IMAGE,
    ogType: "website",
    twitterCard: "summary_large_image",
    jsonLd: [
      ORGANIZATION_LD,
      WEBSITE_LD,
      breadcrumb([
        { name: "Home", url: CANONICAL_DOMAIN },
        { name: "Contact", url: `${CANONICAL_DOMAIN}/contact` },
      ]),
    ],
  },
};

/* ─── Sanity check — exported so tests can introspect ─────────── */
export const PUBLIC_PAGE_KEYS = Object.keys(PUBLIC_PAGE_SEO);
